import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {PortalApi, WebmailApi} from "components/src/api/portal/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useEffect, useRef, useState} from "react";
import {Trash as DeleteIcon} from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());
const portalApi = new PortalApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CreateNewMailProps {
    onClose : OnClose
}

export const CreateNewMailPopup = ({onClose} : CreateNewMailProps) =>
{
    /**
     * Subject of the email
     */
    const [subject, setSubject] = useState<string>();
    /**
     * Body of the email
     */
    const [body, setBody] = useState<string>();
    /**
     * Recipients of the email
     */
    const [recipients, setRecipients] = useState<string[]>([]);
    /**
     * Attachments
     */
    const [attachments, setAttachments] = useState<File[]>([]);
    /*
     * Max size in bytes of all attachments
     */
    const [attachmentsMaxSize, setAttachmentsMaxSize] = useState<number | undefined>();
    /**
     * The error message if some error occurred, which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Reference to the subject element so it can receive focus
     */
    const subjectRef = useRef(null as HTMLInputElement | null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        portalApi.portalControllerGetPortalProperties().then(
            (response) => {
                setAttachmentsMaxSize(response.data.replyAttachmentsMaxSize)
            }
        )
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]);

    const sendMail = () =>
    {
        // sanity checks
        if (subject === undefined || subject.length === 0 || recipients.length === 0 || body === undefined
            || body.length === 0) {
            return;
        }

        // reset error message
        setErrorMessage(undefined);

        setSending(true);

        webmailApi.webmailPortalControllerSendMail(subject, recipients, body, attachments).then(() => {
            onClose(DialogCloseCause.Succes);
        })
        .catch((err: Error | AxiosError) => {
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    function addAttachments(fileList : FileList){
        setAttachments(prevAttachments => [...prevAttachments, ...Array.from(fileList)]);
    }

    function removeAttachment(file : File){
        setAttachments(prevAttachments => prevAttachments.filter(f => f !== file));
    }

    // called when the dialog is closed without sending the mail
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               backdrop="static"
               scrollable={true}
               dialogClassName={"cm-modal-dialog"}
               contentClassName={"cm-modal-content"}
               onShow={() => {subjectRef.current?.focus()}}
        >
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    {t('portal.messages.mail.new-mail-title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column">
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        <Form className="d-flex flex-column flex-grow-1">
                            <FloatingLabel label={t('portal.messages.mail.subject-label')} className="text-secondary mb-3">
                                <Form.Control
                                    type="text"
                                    value={subject}
                                    onChange={e => {setSubject(e.target.value)}}
                                    placeholder="Subject"
                                    ref={subjectRef}
                                />
                            </FloatingLabel>

                            <FloatingLabel label={t('portal.messages.mail.to-label')} className="text-secondary mb-3">
                                <Form.Control
                                    type="email"
                                    value={recipients.join(',')}
                                    placeholder="Recipients"
                                    multiple={true}
                                    onChange={e => setRecipients(e.target.value.split(',').map(s => s.trim()))} />
                            </FloatingLabel>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('portal.messages.mail.attachment-label')}</Form.Label>&nbsp;{attachmentsMaxSize !== undefined &&
                                        <span className="text-secondary">(max. {Utils.formatBytes(attachmentsMaxSize)})</span>}
                                <Form.Control
                                    size="sm"
                                    type="file"
                                    multiple={true}
                                    accept=""
                                    onChange={e => addAttachments(((e.target as HTMLInputElement).files as FileList))}
                                />
                            </Form.Group>

                            <ListGroup>
                                {attachments?.map(file => (
                                    <ListGroup.Item key={file.name} className="border-0">
                                        <DeleteIcon title={t('portal.messages.mail.remove-attachment-title')} onClick={() => {removeAttachment(file)}}/> {file.name}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>

                            <Form.Group className="mb-3 d-flex flex-column flex-grow-1">
                                <Form.Label className="text-secondary">{t('portal.messages.mail.body-label')}</Form.Label>
                                <Form.Control
                                    className="font-monospace flex-grow-1"
                                    as="textarea"
                                    onChange={e => {setBody(e.target.value)}}
                                    value={body}
                                />
                            </Form.Group>
                        </Form>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={sendMail}
                        disabled={StringUtils.isBlank(body) || recipients.length === 0 || subject === undefined || subject.length === 0 || spinnerStateCounter > 0}>
                    {sending && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {sending ? t('portal.messages.mail.sending') : t('portal.messages.mail.send')}
                </Button>
            </Modal.Footer>
        </Modal>
)
}